import {getMetricIdentifiersFromExpressions} from '../../../util/plotHelpers/plotHelpers';
import {type LineConfig} from '../config/types';
import {type BucketedLine} from '../types';
import {recordRumError} from './../../../util/datadog/datadogRum';
import {
  evaluateExpression,
  type Expression,
  metricsInExpression,
} from './../../../util/expr';
import {type Line, type LineWithSource} from './../../../util/plotHelpers';
import {getValue} from './../../../util/runs';
import {type Key} from './../../../util/runTypes';
import {filterRedundantHistories} from './filterRedundantHistories';
import {type SupplementalMetricsByRun} from './types';

export function convertTime(point: {x: number}) {
  // this is the Date.now() value for 01/01/2010
  // as a safeguard I don't want to convert anything to MS if it's already showing up after this date
  // the backend is using Unix seconds for _timestamp so any time we need to convert should be easily less than this number
  if (point.x < 1262332800000) {
    point.x = point.x * 1000;
  } else {
    recordRumError(
      new Error(
        'Attempting to convert _timestamp that may already be converted'
      )
    );
  }
}

export function convertLineDataToTimestamp(lines: LineWithSource[]) {
  lines.forEach(line => {
    // Only convert to timestamp if the line was generated on the frontend (not backend) as the backend handles s => ms conversion directly
    if (line.source === 'frontend') {
      line.data.forEach(p => {
        p.x = p.x * 1000;
      });
    }
    // The backend-smoothing branch in useLines lifts `line.smoothedLineFromBackend`
    // into the renderable lines array AFTER this function runs. Its data lives
    // off to the side as a nested Line object, so the loop above never sees it
    // and the seconds → ms conversion gets skipped. Without this second walk,
    // the smoothed line ends up at x values 1000× too small (year-1970-ish on
    // a datetime axis) while the primary line is correctly in ms. Only convert
    // when the nested line was constructed on the frontend; backend-built
    // smoothed lines (from `backendLine.smoothedLine`) are already in ms.
    const smoothed = line.smoothedLineFromBackend;
    if (smoothed?.source === 'frontend') {
      smoothed.data.forEach(p => {
        p.x = p.x * 1000;
      });
    }
  });
}

/**
 * Evaluate the x expression for each point in the line
 */
export function evaluateXExpressions(
  expression: Expression,
  lines: Line[],
  supplementalMetricsByRun: SupplementalMetricsByRun
) {
  try {
    lines.forEach(line => {
      line.data.forEach(p => {
        const xyData = {
          x: p.x,
          y: p.y,
          ...(p.y0 !== undefined ? {y0: p.y0} : {}),
        };
        p.x = evaluateExpression(expression, {
          ...xyData,
          ...supplementalMetricsByRun[line.name!],
          ...(p.expressionValues as Record<string, any>),
        });
      });
    });
  } catch (e) {
    console.error('Error evaluating x expression', e);
  }
}

/**
 * Any line with a single point where y !== y0 has a valid area to be displayed
 */
export function removeUselessAreaLines<T extends Line>(lines: T[]) {
  return lines.filter(l => {
    if (l.meta.type !== 'area') {
      return true;
    }

    return l.data.some(p => p.y !== p.y0);
  });
}

export function shouldUseLatestRunGrouping(
  workspaceGroupingDepth: number | undefined
) {
  return Number.isFinite(workspaceGroupingDepth);
}

export function filterTopRunLinesByGroup<T extends LineWithSource>(
  lines: T[],
  {
    getGroupKeysByRunsetId,
  }: {
    getGroupKeysByRunsetId: (runSetID: string | undefined) => Key[];
  }
) {
  const selectedGroupIds = new Set<string>();
  const selectedRunIds = new Set<string>();

  for (const line of lines) {
    const run = line.run;
    const runsetId = run?.runsetInfo?.id;
    const groupKeys = getGroupKeysByRunsetId(runsetId);
    if (!run || groupKeys.length === 0) {
      continue;
    }

    const groupValues = groupKeys.map(groupKey => getValue(run, groupKey));
    const groupId = [runsetId ?? '', ...groupValues].join('\u0000');
    if (!selectedGroupIds.has(groupId)) {
      selectedGroupIds.add(groupId);
      selectedRunIds.add(run.id);
    }
  }

  return lines.filter(line => {
    const run = line.run;
    const groupKeys = getGroupKeysByRunsetId(run?.runsetInfo?.id);
    if (!run || groupKeys.length === 0) {
      return true;
    }
    return selectedRunIds.has(run.id);
  });
}

export function determineXMetrics(
  lineConfig: Pick<LineConfig, 'xAxis' | 'xExpression'>
) {
  const xExpressionMetricIdentifiers = lineConfig.xExpression
    ? metricsInExpression(lineConfig.xExpression, false)
    : [];

  return [
    ...new Set(
      xExpressionMetricIdentifiers
        .concat(lineConfig.xAxis, '_step')
        .filter(metric => !!metric)
    ),
  ];
}

export function determineYMetrics(
  lineConfig: Pick<LineConfig, 'yAxis' | 'expressions' | 'xExpression'>
) {
  const {expressionMetricIdentifiers} = getMetricIdentifiersFromExpressions(
    lineConfig.expressions,
    lineConfig.xExpression
  );

  return [
    ...new Set(
      lineConfig.yAxis
        .concat(expressionMetricIdentifiers)
        .filter(metric => !!metric)
    ),
  ];
}

export function checkIfUsingExpressions(lineConfig: LineConfig) {
  return (
    !!lineConfig.xExpression ||
    (lineConfig.expressions && lineConfig.expressions.length > 0)
  );
}

export function checkIfUsingTimeConversion(lineConfig: LineConfig) {
  return (
    (lineConfig.xAxis === '_runtime' ||
      lineConfig.xAxis === '_absolute_runtime') &&
    lineConfig.xExpression == null
  );
}

/**
 * Mutates runs to make them more efficient process
 */
export function preProcessData(data: BucketedLine[], yMetrics: string[]) {
  /**
   * When plotting metrics by custom x-axis we get a bunch of redundant data results. Since the x-axis values come in on the history with each metric we can just strip off the redundant returns and save some processing time.
   *
   * TODO: eliminate these redundant requests in the sampledHistorySpecs
   */
  data.forEach(run => {
    run.history = filterRedundantHistories(run.history, yMetrics);
  });
}
